import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  PersonnelDocumentType,
  PersonnelDocumentTypeRequest,
} from './personnelDocumentTypes';

const ROOT = '/api/personnel-document-types';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Sent whenever the key is present, empty string included — an empty value is the reader
    saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned.
  */
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const personnelDocumentTypesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<PersonnelDocumentType>>(`${ROOT}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<PersonnelDocumentType>(`${ROOT}/${id}`, { signal }),

  /**
   * The ACTIVE checklist, in order — what the Documents panel draws its rows from.
   *
   * Deleted kinds are left out: the checklist is what is asked for now. A document already
   * filed against a deleted kind keeps it and still names it on the panel.
   */
  options: (signal?: AbortSignal) => api<PersonnelDocumentType[]>(`${ROOT}/options`, { signal }),

  create: (request: PersonnelDocumentTypeRequest) =>
    api<PersonnelDocumentType>(ROOT, { method: 'POST', body: request }),

  update: (id: string, request: PersonnelDocumentTypeRequest) =>
    api<PersonnelDocumentType>(`${ROOT}/${id}`, { method: 'PUT', body: request }),

  /** Soft delete: the row leaves the checklist, the documents stay on the records. */
  deactivate: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`${ROOT}/${id}/restore`, { method: 'POST' }),

  /** Soft-deletes every checklist row at once; resolves with how many. Filed documents are untouched. */
  clear: () => api<number>(`${ROOT}/clear-data`, { method: 'POST' }),
};
